/**
 * The time zone list for the company profile.
 *
 * Every zone the browser knows, not a hand-written shortlist. The business places
 * people across South Asia, the Gulf, Europe and Africa, and a curated list is a
 * promise to edit the code the first time somebody opens an office somewhere it
 * missed.
 *
 * Note what this is *for*: the company's stated home zone, used on documents and
 * anywhere a canonical time is needed. It does not decide how times are displayed;
 * that is always the viewer's own clock. See `dates.ts`.
 */

/**
 * A short list for the rare runtime without `Intl.supportedValuesOf`.
 *
 * Not the intended list: only enough that the picker is never empty, so a missing
 * API degrades to "fewer choices" rather than "cannot save the profile".
 */
const FALLBACK_ZONES = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Dhaka',
  'Asia/Manila',
  'Asia/Riyadh',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Warsaw',
  'Africa/Lagos',
  'Africa/Nairobi',
  'America/New_York',
];

const DEFAULT_TIME_ZONE = 'UTC';

let cached: string[] | null = null;

/** Every zone available, sorted. Computed once, because the list does not change. */
export function timeZones(): string[] {
  if (cached) return cached;

  // `supportedValuesOf` is recent enough that a runtime without it is plausible.
  const supported = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;

  const zones = typeof supported === 'function' ? supported('timeZone') : FALLBACK_ZONES;

  // UTC is not always in the list, and it is the one people reach for by name.
  cached = [...new Set([...zones, 'UTC'])].sort((a, b) => a.localeCompare(b));
  return cached;
}

/**
 * The zone's current offset, as "GMT+5:30".
 *
 * Read from Intl at call time rather than stored, because half these zones move twice
 * a year and a written-down offset would be wrong for months at a stretch.
 */
export function zoneOffset(zone: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    // An unknown zone (a value from an older profile, say). Better a bare name than
    // a screen that will not render.
    return '';
  }
}

/** The viewer's own zone, for the "use mine" shortcut. */
export function localZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
}
